import { Injectable } from '@angular/core';
import { Observable, from, of, timer, throwError } from 'rxjs';
import {
  concatMap,
  switchMap,
  map,
  catchError,
  tap,
  toArray,
  retry,
  timeout
} from 'rxjs/operators';
import { FireflyService } from '../../../services/firefly.service';
import { NarrativeStateService } from './narrative-state.service';
import { CustomModelService } from '../../../services/custom-model.service';
import { NarrativeErrorHandlerService } from './narrative-error-handler.service';
import {
  FireflyImageRequest,
  FireflyVideoRequest,
  FireflyJobResponse,
  FireflyJobStatus,
  GeneratedImage,
  GeneratedVideo,
  UploadedImageData,
  NarrativeScene,
  StyleConfiguration
} from '../models/narrative.model';
import { NARRATIVE_CONSTANTS } from '../constants/narrative.constants';
import { NarrativeHelper } from '../utils/narrative-helper';
import { I18nService } from '../../../i18n/i18n.service';
import { LogService } from '../../../services/log.service';

interface VideoGenerationResult {
  sceneId: string;
  video?: GeneratedVideo;
  error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class NarrativeGenerationService {
  private readonly constants = NARRATIVE_CONSTANTS;

  constructor(
    private fireflyService: FireflyService,
    private customModelService: CustomModelService,
    private stateService: NarrativeStateService,
    private errorHandler: NarrativeErrorHandlerService,
    private i18n: I18nService,
    private logService: LogService
  ) { }

  /**
   * 単一シーンの画像を生成
   */
  generateSceneImages(
    scene: NarrativeScene,
    styleConfig: StyleConfiguration
  ): Observable<GeneratedImage[]> {
    // v5カスタムモデル使用時はIM5 APIを使用（style.imageReferenceはIM5では未対応のため対象外）
    const useIm5 = styleConfig.type === 'custom' && styleConfig.modelVersion === 'image5_custom';

    let imageRequest: FireflyImageRequest | undefined;
    const generate$ = useIm5
      ? this.fireflyService.generateAsyncIm5(this.buildImageRequestIm5(scene, styleConfig))
      : this.fireflyService.generateAsync(imageRequest = this.buildImageRequest(scene, styleConfig));

    return generate$.pipe(
      switchMap(response => {
        if (!response.success || !response.jobId) {
          return throwError(() => new Error(this.i18n.t('vnm.err.imageJobStartFailed')));
        }

        if (styleConfig.type === 'custom') {
          // Custom Model log
          const customModel = styleConfig.customModelId
            ? this.customModelService.getModelById(styleConfig.customModelId)
            : null;

          // There's a chance to trigger image generation without a custom model being selected
          // KNOWN ISSUE: Image generation can be triggered without a custom model being selected
          if(customModel) {
            this.logService.logGenerateContent({
              tool: 'visual_narrative_maker',
              contentType: 'image',
              model: styleConfig.modelVersion,
              modelName: customModel?.assetName,
              imageCount: useIm5 ? 1 : imageRequest?.numVariations ?? 1
            }).subscribe();
          }
        }
        else{
          // Standard image model log
          this.logService.logGenerateContent({
            tool: 'visual_narrative_maker',
            contentType: 'image',
            model: imageRequest?.modelVersion,
            imageCount: imageRequest?.numVariations ?? 1
          }).subscribe();
        }

        return this.pollImageGenerationJob(response.jobId);
      }),
      map(statusResponse => this.extractImagesFromResponse(statusResponse)),
      tap(images => {
        if (images.length > 0) {
          this.stateService.addImagesToScene(scene.id, images);
        }
      }),
      catchError(error => {
        this.errorHandler.handleError(error, {
          type: 'image-generation',
          sceneId: scene.id
        });
        return of([]);
      })
    );
  }

  /**
   * 複数シーンの画像を順次生成
   */
  generateMultipleSceneImages(
    scenes: NarrativeScene[],
    styleConfig: StyleConfiguration
  ): Observable<void> {
    return from(scenes).pipe(
      concatMap((scene, index) => {
        this.stateService.updateSceneStatus(scene.id, 'generating-image');
        this.stateService.setImageGenerationState(
          true,
          10 + (90 * index / scenes.length)
        );

        return this.generateSceneImages(scene, styleConfig).pipe(
          tap(() => {
            this.stateService.updateSceneStatus(scene.id, 'completed');
            this.stateService.setImageGenerationState(
              true,
              10 + (90 * (index + 1) / scenes.length)
            );
          })
        );
      }),
      toArray(),
      map(() => void 0),
      tap(() => {
        this.stateService.setImageGenerationState(false, 100);
        this.errorHandler.showSuccess(this.i18n.t('vnm.success.allImagesGenerated'));
      }),
      catchError(error => {
        this.stateService.setImageGenerationState(false, 0);
        return throwError(() => error);
      })
    );
  }

  /**
   * シーンから動画を生成（改善された非同期処理）
   */
  generateVideosFromScenes(scenes: NarrativeScene[]): Observable<VideoGenerationResult[]> {
    const selectedScenes = scenes.filter(scene =>
      scene.images.length > 0 && scene.selectedImageIndex >= 0
    );

    if (selectedScenes.length === 0) {
      this.errorHandler.handleValidationError(this.i18n.t('vnm.err.noGeneratedImages'));
      return of([]);
    }

    this.errorHandler.showInfo(
      this.i18n.t('vnm.info.generatingVideoFromScenes', { count: String(selectedScenes.length) })
    );

    return from(selectedScenes).pipe(
      concatMap((scene, index) =>
        // RxJSのtimerを使用してsetTimeoutを置き換え
        timer(index * NARRATIVE_CONSTANTS.VIDEO_GENERATION.GENERATION_DELAY_MS).pipe(
          switchMap(() => this.generateVideoForScene(scene)),
          map(result => ({
            sceneId: scene.id,
            video: result.video,
            error: result.error
          })),
          tap(() => {
            const progress = ((index + 1) / selectedScenes.length) * 100;
            this.stateService.setVideoGenerationState(true, progress);
          })
        )
      ),
      toArray(),
      tap(results => {
        this.stateService.setVideoGenerationState(false, 100);
        const successCount = results.filter(r => r.video).length;
        if (successCount > 0) {
          this.errorHandler.showSuccess(
            this.i18n.t('vnm.info.scenesGenerated', { count: String(successCount) })
          );
        }
      })
    );
  }

  /**
   * 単一シーンから動画を生成
   */
  private generateVideoForScene(scene: NarrativeScene): Observable<VideoGenerationResult> {
    const selectedImage = scene.images[scene.selectedImageIndex];
    if (!selectedImage) {
      return of({ sceneId: scene.id, error: 'No image selected' });
    }

    const styleConfig = this.stateService.currentState.styleConfig;

    return from(NarrativeHelper.fetchImageAsBlob(selectedImage.url)).pipe(
      switchMap(blob => this.fireflyService.uploadFile(blob)),
      switchMap(uploadResponse => {
        if (!uploadResponse?.fileId) {
          return throwError(() => new Error(this.i18n.t('vnm.err.imageUploadFailed')));
        }

        const request = this.buildVideoRequest(scene, uploadResponse.fileId, styleConfig);
        return this.fireflyService.generateVideoAsyncV3(request);
      }),
      tap(response => {
        if (response?.success && response.jobId) {
          this.logService.logGenerateContent(
            { 
              tool: 'visual_narrative_maker',
              contentType: 'video',
              videoSize: styleConfig.videoSettings?.selectedSize
            }
          ).subscribe();
        }
      }),
      switchMap(response => this.pollVideoGenerationJob(response.jobId)),
      map(videoUrl => {
        if (videoUrl) {
          const video: GeneratedVideo = {
            url: videoUrl,
            id: NarrativeHelper.generateId(),
            generatedAt: new Date()
          };
          this.stateService.setSceneVideo(scene.id, video);
          return { sceneId: scene.id, video };
        }
        return { sceneId: scene.id, error: 'Video generation failed' };
      }),
      catchError(error => {
        this.errorHandler.handleError(error, {
          type: 'video-generation',
          sceneId: scene.id
        });
        return of({ sceneId: scene.id, error: error.message });
      })
    );
  }

  /**
   * 画像生成リクエストを構築
   */
  private buildImageRequest(
    scene: NarrativeScene,
    styleConfig: StyleConfiguration
  ): FireflyImageRequest {
    // カスタムモデル使用時はstyleConfigのmodelVersionを使用、そうでなければデフォルト
    const modelVersion = styleConfig.type === 'custom' && styleConfig.modelVersion
      ? styleConfig.modelVersion
      : this.constants.IMAGE_GENERATION.MODEL_VERSION;

    const request: FireflyImageRequest = {
      modelVersion,
      prompt: scene.prompt,
      negative_prompt: scene.negativePrompt || '',
      numVariations: this.constants.IMAGE_GENERATION.NUM_VARIATIONS,
      size: this.constants.IMAGE_GENERATION.DEFAULT_SIZE,
      visualIntensity: styleConfig.visualIntensity || this.constants.IMAGE_GENERATION.DEFAULT_VISUAL_INTENSITY
    };

    // シード値の設定
    const baseSeed = Math.floor(Math.random() * 1000000);
    request.seeds = Array.from(
      { length: this.constants.IMAGE_GENERATION.NUM_VARIATIONS },
      (_, i) => baseSeed + i
    );

    // スタイル設定
    if (styleConfig.type === 'image' && styleConfig.imageFileId) {
      request.style = {
        imageReference: {
          source: {
            uploadId: styleConfig.imageFileId
          }
        },
        strength: styleConfig.strength || this.constants.STYLE.DEFAULT_STRENGTH
      };
    } else if (styleConfig.type === 'custom' && styleConfig.customModelId) {
      request.customModelId = styleConfig.customModelId;
    }

    return request;
  }

  /**
   * 画像生成リクエストを構築（IM5 / v5カスタムモデル用）
   */
  private buildImageRequestIm5(
    scene: NarrativeScene,
    styleConfig: StyleConfiguration
  ): object {
    return {
      prompt: scene.prompt,
      numVariations: 1, // IM5は現状1枚生成のみ対応
      aspectRatio: '16:9',
      resolutionLevel: '2.4MP',
      modelId: 'firefly_image',
      localeCode: 'ja-JP',
      prompt_reasoner: 'speed',
      modelVersion: styleConfig.modelVersion,
      customModelId: styleConfig.customModelId,
      seeds: [Math.floor(Math.random() * 1000000)]
    };
  }

  /**
   * 動画生成リクエストを構築
   */
  private buildVideoRequest(scene: NarrativeScene, fileId: string, styleConfig: StyleConfiguration): FireflyVideoRequest {
    const request: FireflyVideoRequest = {
      prompt: scene.prompt,
      seeds: [Math.floor(Math.random() * 1000000)],
      localeCode: this.constants.VIDEO_GENERATION.LOCALE_CODE,
      image: {
        conditions: [{
          source: {
            uploadId: fileId
          },
          placement: {
            position: 0
          }
        }]
      },
      sizes: [this.getVideoSize(styleConfig.videoSettings?.selectedSize)],
      output: {
        storeInputs: false
      }
    };

    // 動画設定をマージ：シーン個別設定で明示的に設定された値を優先し、未設定の項目のみデフォルト設定を使用
    const videoSettings: any = {};
    
    // デフォルト設定を基準とし、シーン個別設定で明示的に設定された値で上書き
    const defaultSettings = styleConfig.videoSettings;
    const sceneSettings = scene.videoSettings;
    
    // キーフレーム画像がある場合はカメラモーションのみ有効
    const cameraMotion = sceneSettings?.hasOwnProperty('cameraMotion') 
      ? sceneSettings.cameraMotion 
      : defaultSettings?.cameraMotion;
    if (cameraMotion && cameraMotion !== '') {
      videoSettings.camera_motion = cameraMotion;
    }
    
    if (Object.keys(videoSettings).length > 0) {
      request.videoSettings = videoSettings;
    }

    return request;
  }

  /**
   * 動画サイズを取得
   */
  private getVideoSize(selectedSize?: string): { width: number; height: number } {
    if (selectedSize && this.constants.VIDEO_GENERATION.SIZES[selectedSize]) {
      return this.constants.VIDEO_GENERATION.SIZES[selectedSize];
    }
    return this.constants.VIDEO_GENERATION.DEFAULT_SIZE;
  }

  /**
   * 画像生成ジョブをポーリング
   */
  private pollImageGenerationJob(jobId: string): Observable<FireflyJobStatus> {
    return this.fireflyService.pollJobStatus(jobId).pipe(
      timeout(this.constants.VIDEO_GENERATION.MAX_POLLING_ATTEMPTS * this.constants.VIDEO_GENERATION.POLLING_INTERVAL_MS),
      retry({ count: 2, delay: 1000 })
    );
  }

  /**
   * 動画生成ジョブをポーリング
   */
  private pollVideoGenerationJob(jobId: string): Observable<string | null> {
    return new Observable<string | null>(observer => {
      let attempts = 0;
      const maxAttempts = this.constants.VIDEO_GENERATION.MAX_POLLING_ATTEMPTS;
      const interval = setInterval(() => {
        attempts++;

        if (attempts > maxAttempts) {
          clearInterval(interval);
          observer.error(new Error('ポーリングタイムアウト'));
          return;
        }

        this.fireflyService.getJobStatus(jobId).subscribe({
          next: (statusResponse) => {
            if (statusResponse.status === 'pending' || statusResponse.status === 'running') {
              return;
            }

            clearInterval(interval);

            if (statusResponse.status === 'succeeded') {
              const videoUrl = statusResponse.result?.outputs?.[0]?.video?.url;
              observer.next(videoUrl || null);
              observer.complete();
            } else {
              observer.next(null);
              observer.complete();
            }
          },
          error: (error) => {
            clearInterval(interval);
            observer.error(error);
          }
        });
      }, this.constants.VIDEO_GENERATION.POLLING_INTERVAL_MS);

      return () => {
        clearInterval(interval);
      };
    });
  }

  /**
   * レスポンスから画像を抽出
   */
  private extractImagesFromResponse(response: FireflyJobStatus): GeneratedImage[] {
    if (response.result && response.result.outputs && response.result.outputs.length > 0) {
      return response.result.outputs
        .filter(output => output.image?.url)
        .map(output => ({
          url: output.image!.url,
          id: NarrativeHelper.generateId(),
          generatedAt: new Date()
        }));
    }
    return [];
  }
}